import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { 
    Edit, 
    Trash2, 
    Calendar,
    Users,
    Clock,
    MapPin,
    MoreVertical,
    Eye
} from 'lucide-react';
import EditCourseModal from './EditCourseModal';
import SessionDetailModal from './SessionDetailModal';
import RecurrenceManager from './RecurrenceManager';

export default function CoursesList({ courses, onCourseUpdated, onCourseDeleted, readOnly = false }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSessionsModal, setShowSessionsModal] = useState(false);
    const [showRecurrenceManager, setShowRecurrenceManager] = useState(false);
    const [expandedCourse, setExpandedCourse] = useState(null);

    const handleEdit = (course) => {
        if (readOnly) return;
        setSelectedCourse(course);
        setShowEditModal(true);
    };

    const handleDelete = async (course) => {
        if (readOnly) return;
        if (!window.confirm(t('gym.courses.list.deleteConfirm', { title: course.title }))) {
            return;
        }

        try {
            const api = getAuthenticatedApi();
            await api.delete(`/gym_courses.php?action=delete_course&id=${course.id}`);
            onCourseDeleted();
        } catch (err) {
            console.error('Errore eliminazione corso:', err);
            toast.error(t('gym.courses.errors.deleteError'));
        }
    };

    const handleViewSessions = (course) => {
        setSelectedCourse(course);
        setShowSessionsModal(true);
    };

    const handleManageRecurrences = (course) => {
        if (readOnly) return;
        setSelectedCourse(course);
        setShowRecurrenceManager(true);
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-800 dark:text-green-400', label: t('gym.courses.status.active') },
            paused: { bg: 'bg-yellow-100 dark:bg-yellow-900/20', text: 'text-yellow-800 dark:text-yellow-400', label: t('gym.courses.status.paused') },
            cancelled: { bg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-800 dark:text-red-400', label: t('gym.courses.status.cancelled') }
        };
        
        const badge = badges[status] || badges.active;
        
        return (
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
        );
    };

    const getRecurrenceText = (course) => {
        if (!course.is_recurring) return t('gym.courses.list.notRecurring');
        
        const daysMap = {
            monday: t('gym.calendar.days.monday'),
            tuesday: t('gym.calendar.days.tuesday'),
            wednesday: t('gym.calendar.days.wednesday'),
            thursday: t('gym.calendar.days.thursday'),
            friday: t('gym.calendar.days.friday'),
            saturday: t('gym.calendar.days.saturday'),
            sunday: t('gym.calendar.days.sunday')
        };
        
        if (course.recurrence_days && Array.isArray(course.recurrence_days)) {
            const days = course.recurrence_days.map(d => daysMap[d] || d).join(', ');
            return `${t('gym.courses.list.recurring')}: ${days}`;
        }
        
        return t('gym.courses.list.recurring');
    };

    return (
        <div className="space-y-4">
            {courses.map((course) => (
                <div
                    key={course.id}
                    className="rounded-lg border border-gray-200 bg-white shadow transition-shadow hover:shadow-md dark:border-white/10 dark:bg-gray-900"
                >
                    <div className="p-6">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                {/* Header */}
                                <div className="flex items-center gap-3 mb-2">
                                    {/* Logo Corso o Pallino Colore */}
                                    {course.logo_filename ? (
                                        <img 
                                            src={`/api/serve_image.php?filename=${course.logo_filename}&type=logo&v=${Date.now()}`}
                                            alt={course.title}
                                            className="h-10 w-10 rounded-lg border border-gray-200 object-cover dark:border-white/15"
                                        />
                                    ) : (
                                        <div
                                            className="w-4 h-4 rounded-full"
                                            style={{ backgroundColor: course.color || '#3B82F6' }}
                                        />
                                    )}
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                        {course.title}
                                    </h3>
                                    {course.category && (
                                        <span className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-white/85">
                                            {course.category}
                                        </span>
                                    )}
                                    {getStatusBadge(course.status)}
                                </div>

                                {/* Description */}
                                {course.description && (
                                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                                        {course.description}
                                    </p>
                                )}

                                {/* Info Grid */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <InfoItem
                                        icon={<Calendar size={16} />}
                                        label={t('gym.courses.list.sessions')}
                                        value={`${course.upcoming_sessions || 0} / ${course.total_sessions || 0}`}
                                    />
                                    <InfoItem
                                        icon={<Users size={16} />}
                                        label={t('gym.courses.list.maxParticipants')}
                                        value={course.is_unlimited ? t('gym.courses.list.unlimited') : course.max_participants || t('gym.courses.list.notSet')}
                                    />
                                    <InfoItem
                                        icon={<Clock size={16} />}
                                        label={t('gym.courses.list.recurrence')}
                                        value={getRecurrenceText(course)}
                                    />
                                    {course.is_recurring && course.standard_start_time && (
                                        <InfoItem
                                            icon={<Clock size={16} />}
                                            label={t('gym.courses.list.schedule')}
                                            value={`${course.standard_start_time?.slice(0, 5)} - ${course.standard_end_time?.slice(0, 5)}`}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 ml-4">
                                {course.is_recurring && !readOnly && (
                                    <button
                                        onClick={() => handleManageRecurrences(course)}
                                        className="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                                        title={t('gym.courses.list.manageLessons')}
                                    >
                                        <Calendar size={20} />
                                    </button>
                                )}
                                {!readOnly && (
                                    <>
                                        <button
                                            onClick={() => handleEdit(course)}
                                            className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                                            title={t('gym.courses.list.edit')}
                                        >
                                            <Edit size={20} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(course)}
                                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            title={t('gym.courses.list.delete')}
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Footer Info */}
                        <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 text-sm text-gray-500 dark:border-white/10 dark:text-white/60">
                            <span>
                                {t('gym.courses.list.createdBy')}: {course.created_by_name || 'N/A'}
                            </span>
                            <span>
                                {t('gym.courses.list.createdAt')}: {new Date(course.created_at).toLocaleDateString('it-IT')}
                            </span>
                        </div>
                    </div>
                </div>
            ))}

            {/* Modal Modifica Corso */}
            {!readOnly && showEditModal && selectedCourse && (
                <EditCourseModal
                    isOpen={showEditModal}
                    course={selectedCourse}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedCourse(null);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedCourse(null);
                        onCourseUpdated();
                    }}
                />
            )}

            {/* Modal Sessioni */}
            {showSessionsModal && selectedCourse && (
                <SessionDetailModal
                    isOpen={showSessionsModal}
                    course={selectedCourse}
                    onClose={() => {
                        setShowSessionsModal(false);
                        setSelectedCourse(null);
                    }}
                    onSessionUpdated={onCourseUpdated}
                />
            )}

            {/* Modal Gestione Lezioni */}
            {!readOnly && showRecurrenceManager && selectedCourse && (
                <RecurrenceManager
                    isOpen={showRecurrenceManager}
                    course={selectedCourse}
                    onClose={() => {
                        setShowRecurrenceManager(false);
                        setSelectedCourse(null);
                    }}
                    onRecurrenceUpdated={onCourseUpdated}
                />
            )}
        </div>
    );
}

// Componente InfoItem
function InfoItem({ icon, label, value }) {
    return (
        <div className="flex items-start gap-2">
            <div className="text-gray-400 dark:text-gray-500 mt-0.5">
                {icon}
            </div>
            <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{value}</p>
            </div>
        </div>
    );
}

